<script setup>
import { computed, ref, onMounted } from "vue"
import Layout from "../../../layouts/main.vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"

import * as persianTools from "@persian-tools/persian-tools"
import PersianDate from "persian-date"

import store from "../../../state/store"

const currentUser = computed(() => store.state.user.data)
const bestSellingProducts = computed(() => store.state.bestSellingProducts)
const topSellingStores = computed(() => store.state.topSellingStores)
const lastPendingOrders = computed(() => store.state.lastPendingOrders)

const formattedPrices = (value) => {
  return new Intl.NumberFormat("fa-IR").format(value)
}

const orderDetails = ref({
  total_orders: 0,
  total_sales: 0,
})

const todayPendingOrderDetails = ref({
  total_today_pending_orders: 0,
  total_today_pending_sales: 0,
})

const totalUsers = ref()
const todayRegisteredUsers = ref()

const settlementDetails = ref({
  total_settlement_today: 0,
  total_all_settlement: 0,
})

onMounted(async () => {
  const { data: orderStatsData } = await store.dispatch("getOrderStats")
  orderDetails.value = {
    total_orders: orderStatsData.total_orders ?? 0,
    total_sales: parseFloat(orderStatsData.total_sales) || 0,
  }

  const { data: todayPendingOrderData } = await store.dispatch(
    "getTodayPendingOrder"
  )
  todayPendingOrderDetails.value = {
    total_today_pending_orders: todayPendingOrderData.total_pending_orders ?? 0,
    total_today_pending_sales:
      parseFloat(todayPendingOrderData.total_pending_sales) || 0,
  }

  const { data: totalUsersData } = await store.dispatch("getTotalUsers")
  totalUsers.value = totalUsersData.total_users ?? 0

  const { data: todayRegisteredUsersData } = await store.dispatch(
    "getTodayRegisteredUsers"
  )
  todayRegisteredUsers.value =
    todayRegisteredUsersData.today_registered_users ?? 0

  const { data: settlementStatsData } = await store.dispatch(
    "getSettlementStats"
  )
  settlementDetails.value = {
    total_settlement_today: settlementStatsData.total_settlement_today ?? 0,
    total_all_settlement:
      parseFloat(settlementStatsData.total_all_settlement) || 0,
  }

  await store.dispatch("getBestSellingProducts")
  await store.dispatch("getTopSellingStores")
  await store.dispatch("getLastPendingOrders")
})

const toPersianDateTime = (date) => {
  const [datePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const gregorianDate = new Date(year, month - 1, day)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD")
}

const defaultOptions = ref({
  animationData: animationData,
})
</script>

<template>
  <Layout>
    <b-row>
      <b-col>
        <div class="h-100">
          <b-row class="mb-3 pb-1">
            <b-col cols="12">
              <div class="d-flex align-items-lg-center flex-lg-row flex-column">
                <div class="flex-grow-1">
                  <h4 class="fs-16 mb-1">
                    وقت بخیر {{ currentUser.name }} عزیز!
                  </h4>
                </div>
                <div class="mt-3 mt-lg-0"></div>
              </div>
            </b-col>
          </b-row>

          <b-row>
            <b-col xl="3" md="6">
              <b-card no-body class="card-animate">
                <b-card-body>
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1 overflow-hidden">
                      <p
                        class="text-uppercase fw-medium text-muted text-truncate mb-0"
                      >
                        فروش کل
                      </p>
                    </div>
                  </div>
                  <div
                    class="d-flex align-items-end justify-content-between mt-3"
                  >
                    <div>
                      <h4 class="fs-14 fw-semibold ff-secondary mb-2">
                        تعداد:
                        {{
                          orderDetails
                            ? persianTools.digitsEnToFa(
                                orderDetails.total_orders
                              )
                            : ""
                        }}
                      </h4>
                      <h4 class="fs-14 fw-semibold ff-secondary mb-4">
                        مبلغ:
                        {{
                          orderDetails
                            ? formattedPrices(orderDetails.total_sales)
                            : ""
                        }}
                      </h4>
                      <router-link
                        :to="{
                          name: 'orders',
                        }"
                        >جزئیات بیشتر</router-link
                      >
                    </div>
                    <div class="avatar-sm flex-shrink-0">
                      <span class="avatar-title bg-success rounded fs-3">
                        <i class="bx bx-dollar-circle"></i>
                      </span>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col xl="3" md="6">
              <b-card no-body class="card-animate">
                <b-card-body>
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1 overflow-hidden">
                      <p
                        class="text-uppercase fw-medium text-muted text-truncate mb-0"
                      >
                        فاکتورهای امروز
                      </p>
                    </div>
                  </div>
                  <div
                    class="d-flex align-items-end justify-content-between mt-3"
                  >
                    <div>
                      <h4 class="fs-14 fw-semibold ff-secondary mb-2">
                        تعداد:
                        {{
                          todayPendingOrderDetails
                            ? persianTools.digitsEnToFa(
                                todayPendingOrderDetails.total_today_pending_orders
                              )
                            : ""
                        }}
                      </h4>
                      <h4 class="fs-14 fw-semibold ff-secondary mb-4">
                        مبلغ:
                        {{
                          todayPendingOrderDetails
                            ? formattedPrices(
                                todayPendingOrderDetails.total_today_pending_sales
                              )
                            : ""
                        }}
                      </h4>
                      <router-link
                        :to="{
                          name: 'orders',
                        }"
                        >جزئیات بیشتر</router-link
                      >
                    </div>
                    <div class="avatar-sm flex-shrink-0">
                      <span class="avatar-title bg-info rounded fs-3">
                        <i class="bx bx-shopping-bag"></i>
                      </span>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col xl="3" md="6">
              <b-card no-body class="card-animate">
                <b-card-body>
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1 overflow-hidden">
                      <p
                        class="text-uppercase fw-medium text-muted text-truncate mb-0"
                      >
                        کاربران
                      </p>
                    </div>
                  </div>
                  <div
                    class="d-flex align-items-end justify-content-between mt-3"
                  >
                    <div>
                      <h4 class="fs-14 fw-semibold ff-secondary mb-2">
                        کل:
                        {{
                          totalUsers >= 0
                            ? persianTools.digitsEnToFa(totalUsers)
                            : ""
                        }}
                      </h4>
                      <h4 class="fs-14 fw-semibold ff-secondary mb-4">
                        جدید:
                        {{
                          todayRegisteredUsers >= 0
                            ? persianTools.digitsEnToFa(todayRegisteredUsers)
                            : ""
                        }}
                      </h4>
                      <router-link
                        :to="{
                          name: 'users',
                        }"
                        >جزئیات بیشتر</router-link
                      >
                    </div>
                    <div class="avatar-sm flex-shrink-0">
                      <span class="avatar-title bg-warning rounded fs-3">
                        <i class="bx bx-user-circle"></i>
                      </span>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col xl="3" md="6">
              <b-card no-body class="card-animate">
                <b-card-body>
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1 overflow-hidden">
                      <p
                        class="text-uppercase fw-medium text-muted text-truncate mb-0"
                      >
                        پرداختی ها به فروشگاه ها
                      </p>
                    </div>
                  </div>
                  <div
                    class="d-flex align-items-end justify-content-between mt-3"
                  >
                    <div>
                      <h4 class="fs-14 fw-semibold ff-secondary mb-2">
                        کل:
                        {{
                          settlementDetails
                            ? formattedPrices(
                                settlementDetails.total_all_settlement
                              )
                            : ""
                        }}
                      </h4>
                      <h4 class="fs-14 fw-semibold ff-secondary mb-4">
                        امروز:
                        {{
                          settlementDetails
                            ? formattedPrices(
                                settlementDetails.total_settlement_today
                              )
                            : ""
                        }}
                      </h4>
                      <router-link
                        :to="{
                          name: 'stores',
                        }"
                      >جزئیات بیشتر</router-link>
                    </div>
                    <div class="avatar-sm flex-shrink-0">
                      <span class="avatar-title bg-danger rounded fs-3">
                        <i class="bx bx-store"></i>
                      </span>
                    </div>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col xl="7">
              <b-card no-body>
                <b-card-header class="align-items-center d-flex">
                  <b-card-title class="mb-0 flex-grow-1">
                    پرفروش ترین محصولات
                  </b-card-title>
                </b-card-header>

                <b-card-body>
                  <div class="table-responsive table-card">
                    <table
                      class="table table-hover table-centered align-middle table-nowrap mb-0"
                    >
                      <tbody
                        v-if="
                          bestSellingProducts.loading ||
                          !bestSellingProducts.data.length
                        "
                      >
                        <tr>
                          <td colspan="4">
                            <div class="text-center">
                              <b-spinner
                                v-if="bestSellingProducts.loading"
                                variant="dark"
                                style="width: 3rem; height: 3rem"
                              ></b-spinner>
                              <div v-else>
                                <lottie
                                  class="avatar-xl"
                                  colors="primary:#121331,secondary:#08a88a"
                                  :options="defaultOptions"
                                  :height="75"
                                  :width="75"
                                />
                                <h5 class="mt-2">هیچ محصولی وجود ندارد!</h5>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr
                          v-for="(product, index) of bestSellingProducts.data"
                          :key="index"
                        >
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-sm bg-light rounded p-1 me-2">
                                <img
                                  :src="product.image"
                                  :alt="product.name"
                                  class="img-fluid d-block"
                                />
                              </div>
                              <div>
                                <h5 class="fs-14 my-1">
                                  <router-link
                                    :to="{
                                      name: 'product.details',
                                      params: { id: product.id },
                                    }"
                                    class="text-reset"
                                    >{{ product.name }}
                                  </router-link>
                                </h5>
                              </div>
                            </div>
                          </td>
                          <td>
                            <h5 class="fs-14 my-1 fw-normal">
                              {{ formattedPrices(product.price) }}
                            </h5>
                            <span class="text-muted">قیمت</span>
                          </td>
                          <td>
                            <h5 class="fs-14 my-1 fw-normal">
                              {{
                                persianTools.digitsEnToFa(
                                  product.total_quantity
                                )
                              }}
                            </h5>
                            <span class="text-muted">سفارش</span>
                          </td>

                          <td>
                            <h5 class="fs-14 my-1 fw-normal">
                              {{ formattedPrices(product.total_price) }}
                            </h5>
                            <span class="text-muted">کل فروش</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>

            <b-col xl="5">
              <b-card no-body class="card-height-100">
                <b-card-header class="align-items-center d-flex">
                  <b-card-title class="mb-0 flex-grow-1"
                    >فروشندگان برتر</b-card-title
                  >
                </b-card-header>

                <b-card-body>
                  <div class="table-responsive table-card">
                    <table
                      class="table table-centered table-hover align-middle table-nowrap mb-0"
                    >
                      <tbody
                        v-if="
                          topSellingStores.loading ||
                          !topSellingStores.data.length
                        "
                      >
                        <tr>
                          <td colspan="3">
                            <div class="text-center">
                              <b-spinner
                                v-if="topSellingStores.loading"
                                variant="dark"
                                style="width: 3rem; height: 3rem"
                              ></b-spinner>
                              <div v-else>
                                <lottie
                                  class="avatar-xl"
                                  colors="primary:#121331,secondary:#08a88a"
                                  :options="defaultOptions"
                                  :height="75"
                                  :width="75"
                                />
                                <h5 class="mt-2">هیچ فروشگاهی وجود ندارد!</h5>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr
                          v-for="(store, index) of topSellingStores.data"
                          :key="index"
                        >
                          <td>
                            <div class="d-flex align-items-center">
                              <div>
                                <h5 class="fs-14 my-1 fw-medium">
                                  <router-link
                                    :to="{
                                      name: 'stores.details',
                                      params: { id: store.id },
                                    }"
                                    class="text-reset"
                                    >{{ store.first_name }}
                                  </router-link>
                                </h5>
                                <span class="text-muted">{{
                                  store.email
                                }}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span class="text-muted">
                              {{
                                persianTools.digitsEnToFa(
                                  store.total_products_sold
                                )
                              }}
                              عدد
                            </span>
                          </td>

                          <td>
                            <span class="text-muted">{{
                              formattedPrices(store.total_sales)
                            }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>

          <b-row>
            <b-col xl="12">
              <b-card no-body>
                <b-card-header class="align-items-center d-flex">
                  <b-card-title class="mb-0 flex-grow-1"
                    >سفارشات اخیر (در انتظار تایید)</b-card-title
                  >
                </b-card-header>

                <b-card-body>
                  <div class="table-responsive table-card">
                    <table
                      class="table table-borderless table-centered align-middle table-nowrap mb-0"
                    >
                      <thead class="text-muted table-light">
                        <tr>
                          <th scope="col">شماره سفارش</th>
                          <th scope="col">مشتری</th>
                          <th scope="col">تاریخ ثبت سفارش</th>
                          <th scope="col">بازه زمانی</th>
                          <th scope="col">مبلغ</th>
                        </tr>
                      </thead>

                      <tbody
                        v-if="
                          lastPendingOrders.loading ||
                          !lastPendingOrders.data.length
                        "
                      >
                        <tr>
                          <td colspan="5">
                            <div class="text-center">
                              <b-spinner
                                v-if="lastPendingOrders.loading"
                                variant="dark"
                                style="width: 3rem; height: 3rem"
                              ></b-spinner>
                              <div v-else>
                                <lottie
                                  class="avatar-xl"
                                  colors="primary:#121331,secondary:#08a88a"
                                  :options="defaultOptions"
                                  :height="75"
                                  :width="75"
                                />
                                <h5 class="mt-2">هیچ سفارشی وجود ندارد!</h5>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                      <tbody v-else>
                        <tr
                          v-for="(order, index) of lastPendingOrders.data"
                          :key="index"
                        >
                          <td>
                            <router-link
                              :to="{
                                name: 'orders.details',
                                params: { id: order.id },
                              }"
                              class="fw-medium link-primary"
                              >#
                              {{
                                persianTools.digitsEnToFa(order.trackingCode)
                              }}
                            </router-link>
                          </td>
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="flex-grow-1">
                                {{ order.user }}
                              </div>
                            </div>
                          </td>
                          <td>{{ toPersianDateTime(order.dateTime) }}</td>
                          <td>
                            <span class="text-success">
                              {{ order.timeRange }}</span
                            >
                          </td>
                          <td>{{ formattedPrices(order.totalPrice) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </b-card-body>
              </b-card>
            </b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </Layout>
</template>
